import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Lock, Check, CheckCircle2, ArrowLeft } from 'lucide-react';
import { AuthShell, HudLabel, HudInput, PrimaryButton, AuthNotice, passwordStrength, C } from '@/components/auth/AuthUI';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatApiErrorDetail(detail) {
  if (detail == null) return 'Something went wrong. Please try again.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e && typeof e.msg === 'string' ? e.msg : JSON.stringify(e))).filter(Boolean).join(' ');
  if (detail && typeof detail.msg === 'string') return detail.msg;
  return String(detail);
}

export default function ResetPassword() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const [pw, setPw] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);
  const strength = passwordStrength(pw);

  useEffect(() => { if (!token) setError('This reset link is invalid or incomplete. Please request a new one.'); }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 8 || pw !== confirm || !token) {
      if (pw.length < 8) setError('Use at least 8 characters.');
      else if (pw !== confirm) setError("Passwords don't match.");
      return;
    }
    setBusy(true); setError('');
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: pw }, { withCredentials: true });
      setDone(true);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Couldn't reset your password. Please request a new link.");
      setBusy(false);
    }
  };

  if (done) {
    return (
      <AuthShell subtitle="All set" logoTestId="reset-sprout">
        <div className="space-y-5 text-center" data-testid="reset-success">
          <div className="mx-auto flex items-center justify-center rounded-full" style={{ width: 60, height: 60, background: 'rgba(180,211,48,0.16)', border: `1px solid ${C.lime}` }}>
            <CheckCircle2 className="w-7 h-7" style={{ color: C.lime }} />
          </div>
          <div>
            <h2 className="text-xl font-bold" style={{ fontFamily: 'Outfit, sans-serif', color: C.cream }}>Password updated</h2>
            <p className="mt-2 text-sm" style={{ color: 'rgba(250,246,236,0.7)' }}>You can now sign in with your new password.</p>
          </div>
          <PrimaryButton type="button" onClick={() => navigate('/login')} dataTestid="reset-back-to-login">Back to sign in</PrimaryButton>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell subtitle="Choose a new password" logoTestId="reset-sprout">
      <form onSubmit={submit} className="space-y-4">
        <div>
          <HudLabel>New password</HudLabel>
          <HudInput icon={Lock} secure autoComplete="new-password" placeholder="At least 8 characters"
            value={pw} onChange={(e) => setPw(e.target.value)} dataTestid="reset-newpw-input" />
          {pw ? (
            <div className="mt-2" data-testid="reset-strength">
              <div className="flex gap-1.5">
                {[0, 1, 2, 3].map((i) => (
                  <span key={i} className="flex-1 rounded-full transition-all" style={{
                    height: 4, background: i < strength.score ? strength.color : 'rgba(250,246,236,0.14)',
                    boxShadow: i < strength.score ? `0 0 8px ${strength.color}` : 'none' }} />
                ))}
              </div>
              <p className="text-xs mt-1 font-semibold" style={{ color: strength.color }}>{strength.label}</p>
            </div>
          ) : null}
        </div>
        <div>
          <HudLabel>Confirm new password</HudLabel>
          <HudInput icon={Lock} secure autoComplete="new-password" placeholder="Re-enter your password"
            value={confirm} onChange={(e) => setConfirm(e.target.value)} dataTestid="reset-confirm-input" />
          {confirm && confirm !== pw ? <p className="text-xs mt-1" style={{ color: C.terracotta }}>Passwords don't match.</p> : null}
        </div>

        {error ? <AuthNotice dataTestid="reset-error"><span className="inline-flex items-center gap-2"><Lock className="w-4 h-4" />{error}</span></AuthNotice> : null}

        <PrimaryButton type="submit" disabled={busy || !token || pw.length < 8 || pw !== confirm} dataTestid="reset-submit">
          {busy ? 'Updating…' : 'Update password'} <Check className="w-4 h-4" />
        </PrimaryButton>
        <button type="button" onClick={() => navigate('/login')} className="w-full inline-flex items-center justify-center gap-1.5 text-sm pt-1"
          style={{ color: 'rgba(250,246,236,0.65)' }}><ArrowLeft className="w-4 h-4" /> Back to sign in</button>
      </form>
    </AuthShell>
  );
}
